import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import type { EmailResultsProps } from '@/@types/email';
import { Button } from '@/components/ui/button';
import CryptoJS from 'crypto-js';
import { Check, Mail } from 'lucide-react';
import { useTranslation } from 'next-i18next';

export const EmailCalculatorResults: React.FC<{
  data: EmailResultsProps;
  csrfToken: string;
}> = ({ data, csrfToken }) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const [email, setEmail] = useState('');
  const [showError, setShowError] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [userSubmittedData, setUserSubmittedData] = useState(false);

  useEffect(() => {
    setUserSubmittedData(data !== null && data !== undefined);

    if (data === null || data === undefined) {
      setEmail('');
      setEmailSent(false);
    }
  }, [data]);

  useEffect(() => {
    if (userSubmittedData) {
      setEmailSent(false);
      setShowError(false);
    }
  }, [email]);

  async function handleSendEmail() {
    setShowError(false);
    setIsSendingEmail(true);

    const isValidEmail = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;

    if (email && email !== '' && email.match(isValidEmail)) {
      const emailSubject = t('calculatorsPage.email.subject');

      const dataJSON = JSON.stringify(data);
      const encryptedData = CryptoJS.AES.encrypt(
        dataJSON,
        'academy',
      ).toString();

      const url = `${process.env.NEXT_PUBLIC_APP_URL}/api/academy-klingon-ewok`;
      const requestData = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          emailSubject,
          email,
          data: encryptedData,
          locale,
        }),
      };

      try {
        const dataRes = await fetch(url, requestData);

        if (dataRes.status === 200) {
          setEmailSent(true);
          setShowError(false);
        } else {
          setShowError(true);
        }
      } catch (e) {
        console.log(e);
        setShowError(true);
      }
    } else {
      setShowError(true);
    }

    setIsSendingEmail(false);
  }

  const getSendButtonStyle = () => {
    if (!userSubmittedData) {
      return 'bg-[#193CEB] cursor-not-allowed text-white';
    }

    if (isSendingEmail) {
      return 'bg-[#193CEB] cursor-not-allowed';
    }

    if (emailSent) {
      return 'bg-[#193CEB]';
    }

    return 'bg-[#193CEB] text-white';
    // return "bg-gradient-to-r from-[#0082FF] via-[#0082FF] to-[#00FFFF] from-[-79.13%] via-[-2.45%] to-[146.94%]";
  };

  const getSendButtonText = (): string => {
    if (emailSent) {
      return t('calculatorsPage.email.sent');
    }

    if (isSendingEmail) {
      return t('calculatorsPage.email.sending');
    }

    return t('calculatorsPage.email.send');
  };

  return (
    <div className="absolute bottom-0 pb-10 lg:w-full">
      <h2 className="text-brandAlt-950 mb-3 lg:text-xl">
        {t('calculatorsPage.email.sendResultToEmail')}
      </h2>

      <div className="mt-0 flex w-full max-w-md flex-col gap-x-4 sm:flex-row">
        <div className="focus:ring-brandAlt-500 inline-flex h-[56px] rounded-md border-2 border-[#E1E8EB] text-[#13191B] placeholder-[#80939B]">
          <label htmlFor="email" id="email" className="sr-only"></label>
          <Mail
            className="pointer-events-none m-auto ml-5 w-5"
            color="#0085C9"
          />

          <input
            id="email-address"
            name="email"
            type="email"
            autoComplete="email"
            required
            disabled={!userSubmittedData}
            className="min-w-0 flex-auto rounded-xl border-0 px-4 py-4 text-lg focus:ring-0 sm:leading-6"
            placeholder={t('enterYourEmail')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <Button
          type="submit"
          className={`focus-visible:outline-brandAlt-500 inline-flex h-[56px]	items-center justify-center rounded-md px-3.5 py-2.5 font-semibold text-white ${getSendButtonStyle()} mt-3 w-1/3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-100 sm:mt-0`}
          disabled={!userSubmittedData || !email || isSendingEmail || emailSent}
          onClick={handleSendEmail}
        >
          {isSendingEmail && (
            <div className="mr-2 animate-spin">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-loader-circle"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            </div>
          )}
          {emailSent && (
            <Check color="white" className="pointer-events-none mr-2 w-5" />
          )}
          {getSendButtonText()}
        </Button>
      </div>
      {showError && (
        <div className="mt-2 text-red-600">
          {t('calculatorsPage.email.errorSendingEmail')}
        </div>
      )}
    </div>
  );
};
